import { methodology } from './data/catalog'
import { calculate } from './calculation'
import type { CalculationInput, CalculationResult, HistoryEntry } from './types'

export const HISTORY_KEY = 'cenove-ukazatele-vak:history:v1'
export const HISTORY_LIMIT = 10

function storageAvailable(): boolean {
  try {
    const key = '__vak_storage_test__'
    window.localStorage.setItem(key, key)
    window.localStorage.removeItem(key)
    return true
  } catch {
    return false
  }
}

export function loadHistory(): HistoryEntry[] {
  if (typeof window === 'undefined' || !storageAvailable()) return []
  try {
    const raw = window.localStorage.getItem(HISTORY_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw) as unknown
    if (!Array.isArray(parsed)) return []
    return parsed
      .filter((entry): entry is HistoryEntry => Boolean(
        entry && typeof entry === 'object' && 'id' in entry && 'input' in entry && 'result' in entry,
      ))
      .slice(0, HISTORY_LIMIT)
  } catch {
    return []
  }
}

function persist(entries: HistoryEntry[]): HistoryEntry[] {
  const limited = entries.slice(0, HISTORY_LIMIT)
  if (typeof window !== 'undefined' && storageAvailable()) {
    window.localStorage.setItem(HISTORY_KEY, JSON.stringify(limited))
  }
  return limited
}

export function addHistory(input: CalculationInput, result: CalculationResult): HistoryEntry[] {
  const now = new Date().toISOString()
  const entry: HistoryEntry = {
    id: globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(16).slice(2)}`,
    dataVersion: methodology.meta.dataVersion,
    input: structuredClone(input),
    result: structuredClone(result),
    createdAt: now,
  }
  return persist([entry, ...loadHistory()])
}

export function removeHistory(id: string): HistoryEntry[] {
  return persist(loadHistory().filter((entry) => entry.id !== id))
}

export function clearHistory(): HistoryEntry[] {
  if (typeof window !== 'undefined' && storageAvailable()) window.localStorage.removeItem(HISTORY_KEY)
  return []
}

export function isHistoryOutdated(entry: HistoryEntry): boolean {
  return entry.dataVersion !== methodology.meta.dataVersion
}

export function refreshOutdatedHistory(entries = loadHistory()): HistoryEntry[] {
  const refreshed = entries.map((entry) => {
    if (!isHistoryOutdated(entry)) return entry
    try {
      return {
        ...entry,
        dataVersion: methodology.meta.dataVersion,
        result: calculate(entry.input),
      }
    } catch {
      return entry
    }
  })
  return persist(refreshed)
}
